import { useTranslation } from 'react-i18next';

/**
 * Which role set the page behind the guard needs. Decides the copy: a
 * retoucher who follows a Relations link is told the page is for sales
 * staff, not that they "need admin permissions" (an account_sales user
 * would see the same page without being an admin either).
 */
export type ForbiddenRequired = 'admin' | 'sales_staff' | 'retouch_staff';

interface ForbiddenProps {
  /**
   * True when the viewer's session is impersonating a shop user (the role
   * ceiling strips admin roles from an impersonated principal, so this is
   * the page any admin who follows an /admin/... link while impersonating
   * always lands on). Swaps the generic "you're not an admin" copy for one
   * that names the actual cause. Optional so Forbidden stays usable as a
   * plain permission page wherever impersonation isn't relevant.
   */
  impersonating?: boolean;
  /** Role set the denied page requires; defaults to the shop admins. */
  required?: ForbiddenRequired;
}

const COPY: Record<ForbiddenRequired, { title: string; message: string }> = {
  admin: { title: 'admin.forbidden.title', message: 'admin.forbidden.message' },
  sales_staff: { title: 'admin.forbidden.title_staff', message: 'admin.forbidden.message_sales_staff' },
  retouch_staff: { title: 'admin.forbidden.title_staff', message: 'admin.forbidden.message_retouch_staff' },
};

const Forbidden = ({ impersonating = false, required = 'admin' }: ForbiddenProps) => {
  const { t } = useTranslation();
  const copy = COPY[required];
  return (
    <div className="container mt-5">
      <div className="notification is-danger">
        <h1 className="title is-4">{t(copy.title)}</h1>
        <p>{t(impersonating ? 'admin.forbidden.message_impersonating' : copy.message)}</p>
      </div>
    </div>
  );
};

export default Forbidden;
